import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { type FriendCheckSubmission } from "../backend/api";
import { queryOptions_friendCheckSubmissions } from "../query/functions/friend-check/submissions";
import { FriendCheckService } from "../services/friend-check.service";
import { authSelectors } from "../store/auth";
import { useAuthStore } from "../store/auth";

export type FriendCheckState = FriendCheckSubmission[];

interface FriendCheckStateLoading {
	isLoading: true;
	data: undefined;
	service: undefined;
}

interface FriendCheckStateLoaded {
	isLoading: false;
	data: FriendCheckState;
	service: FriendCheckService;
}

export type FriendCheckStateResult = FriendCheckStateLoading | FriendCheckStateLoaded;

export function useFriendCheck(): FriendCheckStateResult {
	const isAuthenticated = useAuthStore(useShallow(authSelectors.isAuthenticated));

	const { data: friendCheckState, isLoading } = useQuery({
		...queryOptions_friendCheckSubmissions(),
		enabled: isAuthenticated,
	});

	const friendCheckService = useMemo(
		() => (friendCheckState ? new FriendCheckService(friendCheckState) : undefined),
		[friendCheckState],
	);

	if (isLoading) {
		return { isLoading: true, data: undefined, service: undefined };
	}

	if (!friendCheckState || !friendCheckService) {
		return { isLoading: true, data: undefined, service: undefined };
	}

	return { isLoading: false, data: friendCheckState, service: friendCheckService };
}
